import { useEffect, useMemo, useState } from 'react'
import { CakeSlice } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { type PageSize } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { SubscribersService, type Subscriber } from '@/services/modules/SubscribersService'
import { toast } from '@/components/ui/Toast'
import type { PaginationMeta } from '@/types/api'

/** Primeiro e último dia do mês corrente em YYYY-MM-DD. */
function currentMonthRange() {
  const now = new Date()
  const first = new Date(now.getFullYear(), now.getMonth(), 1)
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return { from: fmt(first), to: fmt(last) }
}

/**
 * Formata YYYY-MM-DD como DD/MM/YYYY direto da string, sem criar Date —
 * evita o deslocamento de fuso (UTC→local) que jogava 01/06 para 31/05.
 */
function formatYmd(ymd: string): string {
  const [y, m, d] = ymd.split('-')
  return `${d}/${m}/${y}`
}

/** Data em que o assinante completa 18 anos (nascimento + 18 anos). */
function comingOfAgeDate(birthDate: string | null): string {
  if (!birthDate) return '—'
  const [y, m, d] = birthDate.split('-')
  if (!y || !m || !d) return '—'
  return `${d}/${m}/${Number(y) + 18}`
}

/** Nascimento formatado sem fuso (a partir do YYYY-MM-DD cru). */
function birthDateBR(birthDate: string | null): string {
  if (!birthDate) return '—'
  return formatYmd(birthDate)
}

export function ReportsAgePage() {
  const init = useMemo(currentMonthRange, [])
  const [from, setFrom] = useState(init.from)
  const [to, setTo] = useState(init.to)
  const [applied, setApplied] = useState(init)

  const [rows, setRows] = useState<Subscriber[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<PageSize>(25)

  useEffect(() => {
    let alive = true
    setLoading(true)
    SubscribersService.reportComingOfAge({ from: applied.from, to: applied.to, page, size })
      .then((res) => { if (!alive) return; setRows(res.data); setMeta(res.meta) })
      .catch(() => toast.error('Erro ao carregar o relatório.'))
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [applied, page, size])
  useEffect(() => { setPage(1) }, [applied, size])

  function apply() {
    if (from && to && from > to) {
      toast.error({ title: 'Período inválido', description: 'A data inicial não pode ser maior que a final.' })
      return
    }
    setApplied({ from, to })
  }

  const columns: DataTableColumn<Subscriber>[] = [
    { key: 'name', header: 'Nome', alwaysVisible: true, render: (s) => (
      <div className="flex flex-col">
        <span className="font-medium text-gray-800">{s.name}</span>
        <span className="text-[11px] text-gray-400">#{s.code}</span>
      </div>
    ) },
    { key: 'type', header: 'Tipo', align: 'center', render: (s) => (
      <Badge variant={s.type === 'TITULAR' ? 'blue' : 'gray'}>{s.type}</Badge>
    ) },
    { key: 'cpf', header: 'CPF', align: 'center', render: (s) => s.formatted?.cpf ?? s.cpf ?? '—' },
    { key: 'birthDate', header: 'Nascimento', align: 'center', render: (s) => s.formatted?.birthDate ?? birthDateBR(s.birthDate) },
    { key: 'coa', header: 'Faz 18 anos em', align: 'center', render: (s) => comingOfAgeDate(s.birthDate) },
    { key: 'company', header: 'Empresa', render: (s) => s.company?.name ?? '—' },
  ]

  const exp = useDataTableExport<Subscriber>({
    filename: `maioridade-${applied.from}_a_${applied.to}`,
    service: { list: (p) => SubscribersService.reportComingOfAge({ from: applied.from, to: applied.to, ...p }) },
    columns: [
      { header: 'Código', accessor: (s) => s.code ?? '' },
      { header: 'Nome', accessor: (s) => s.name },
      { header: 'Tipo', accessor: (s) => s.type },
      { header: 'CPF', accessor: (s) => s.formatted?.cpf ?? s.cpf ?? '' },
      { header: 'Nascimento', accessor: (s) => s.formatted?.birthDate ?? birthDateBR(s.birthDate) },
      { header: 'Faz 18 anos em', accessor: (s) => comingOfAgeDate(s.birthDate) },
      { header: 'Empresa', accessor: (s) => s.company?.name ?? '' },
    ],
  })

  return (
    <>
      <PageHeader
        title="Relatório de Maioridade"
        subtitle="Titulares e dependentes que completam 18 anos no período"
        crumbs={[{ label: 'Comercial' }, { label: 'Relatórios' }, { label: 'Maioridade' }]}
      />

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md bg-white p-4 ring-1 ring-gray-200">
        <Input label="De" type="date" value={from} onChange={(e) => setFrom(e.target.value)} wrapperClassName="w-44" />
        <Input label="Até" type="date" value={to} onChange={(e) => setTo(e.target.value)} wrapperClassName="w-44" />
        <Button onClick={apply}>Aplicar</Button>
        <div className="ml-auto flex items-center gap-2 text-sm text-gray-500">
          <CakeSlice size={16} className="text-brand-500" />
          {meta?.total ?? 0} assinante(s) no período
        </div>
      </div>

      <DataTable
        tableId="reports.age"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={(s) => s.id}
        emptyMessage="Nenhum assinante completa 18 anos no período."
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />
    </>
  )
}

export default ReportsAgePage
